/**
 * Mine slice — MY GAMES' personal facts (DESIGN-CHANGELOG 2026-09-03 "MY GAMES:
 * the page about you"; docs/porting/AA.md phase 2; migration 3060).
 *
 * Two kinds of data, both ACCOUNT facts keyed by user key rather than by crew,
 * which is why they are not in the boards slice beside `potentials`/`tiers`
 * (those have been per-crew since 3016):
 *
 *   backlog   what you want to play — yours alone. `backlog[k]` is populated
 *             for the signed-in key only; the table admits nobody else's rows.
 *   scores    0–10, a PLAYED axis separate from the S/A/B/C call — and THE
 *             PARTY SEES IT. `scores[k]` is populated for every active member
 *             of the active crew, which is what the notice board reads
 *             (`Mike scored it 10.`). The map is replaced whole on every read,
 *             so a member who cleared a score, or left, falls out of it.
 *
 * Plus the view state of the four surfaces built on them: the BACKLOG page's
 * ownership tab, query and armed ✕; MY SCORES' query (`rtQ`); the YOUR SCORE
 * modal (`scId`);
 * TRACK A GAME (`trkOpen` / `trkQ` / `trkRows` / `trkState`). `bkQ` and `rtQ`
 * are deliberately NOT `BoardKey` queries: the design keeps both pages' lens
 * out of the shared `bq` ("it is not a board").
 *
 * A SCORE IS NOT A BACKLOG FACT (DESIGN-CHANGELOG 2026-09-12 "Scoring is not a
 * backlog privilege"). `setScore` always wrote `scores[me][id]` for any id;
 * what changed is who can reach it — GAME DETAIL carries the control now and
 * MY SCORES is the ledger of the result. A re-score keeps the number it
 * replaced (`prev`, migration 3067), so that ledger can say what changed.
 *
 * TRACK A GAME IS A STEAM SEARCH (Marcus, 2026-09-04: "track a game search
 * should basically be a steam search"). The prototype's picker lists the
 * catalog; the app asks Steam the same way Discover does — `repo.fetchBrowse`,
 * term only, no tags, no narrowing, page 0 — and the selector lays Steam's
 * rows under the catalog's own matches. The picker keeps its OWN result set
 * and state rather than borrowing Discover's `live` / `bState`: the two
 * surfaces can be open on different questions, and a picker search must never
 * rewrite what Discover is showing behind the modal. Same debounce and
 * minimum as Discover's box so the two feel like one control.
 *
 * Every write is optimistic through `mutate` and keyed per game, like a vote —
 * two shelves in a row on two games are two independent writes. The one
 * exception is the WISHLIST IMPORT (`wishImport`, docs/DISCOVERY.md §7.1a): up
 * to hundreds of games at once, so it is one bulk write under its own key.
 */

import type { StateCreator } from 'zustand';

import type { BacklogEntry, Game, GameId, ScoreRec } from '../../lib/types.ts';
import { R_ARM_MS } from '../../lib/round.ts';
import type { SteamWishlistResult, WishlistGame } from '../../repo/Repository.ts';
import { mutate, storeApi } from '../mutate.ts';
import type { QdStore } from '../types.ts';
import { MINT_CONCURRENCY, provisionalGame, settlePooled } from './boards.ts';
import { SEARCH_DEBOUNCE_MS } from './ui.ts';

/**
 * The wishlist import's toasts. Written repo-side in the house voice (Marcus,
 * 2026-09-15: match the GOTY auto-fill rather than wait on the design); the
 * prototype has no wishlist control, so a later design pass may replace them.
 */
export const WISH_COPY = {
  unlinked: 'LINK STEAM TO PULL IN YOUR WISHLIST',
  private: 'STEAM GAME DETAILS ARE PRIVATE — SET THEM PUBLIC TO PULL IN YOUR WISHLIST',
  // `open: false` from a profile we last saw public: private and empty are
  // indistinguishable on the wire, so the copy owns up to both.
  closed: "NOTHING ON YOUR STEAM WISHLIST — OR IT'S PRIVATE",
  none: 'NO GAMES ON YOUR STEAM WISHLIST',
  nothingNew: 'YOUR WISHLIST IS ALREADY ON YOUR BACKLOG',
  failed: "✕ COULDN'T ADD YOUR WISHLIST — TRY AGAIN",
  upstream: "✕ STEAM DIDN'T ANSWER — TRY AGAIN",
  added: (n: number) => n + (n === 1 ? ' GAME' : ' GAMES') + ' FROM YOUR WISHLIST ON YOUR BACKLOG',
};

/** Discover's threshold, shared: under this there is no question to ask. */
export const TRACK_MIN_CHARS = 2;

/**
 * Record one wishlist read for the WISHLISTED tab — shared by the passive
 * `ensureWishlist` and by `wishImport`, which pays for the same call.
 *
 * `open: false` WRITES `wishIds: null`, NOT AN EMPTY SET, and that is the whole
 * reason this is a function rather than two inline `set`s that could drift.
 * Steam cannot tell a private wishlist from an empty one, so the only honest
 * record of that answer is "we still cannot say what is on it".
 */
function publishWishlist(
  set: (p: Partial<MineSlice>) => void,
  res: SteamWishlistResult,
): void {
  set(
    res.open
      ? { wishIds: new Set(res.games.map((g) => g.appid)), wishClosed: false }
      : { wishIds: null, wishClosed: true },
  );
}

/**
 * What the picker's Steam half is doing. `''` = nothing asked (term under the
 * minimum); `busy` = in the air; `ok` = answered; `err` = Steam could not be
 * reached and the rows are the catalog's alone. `err` is Discover's own
 * reading of a dead browse (`!fromNetwork && items.length === 0`).
 */
export type TrackState = '' | 'busy' | 'ok' | 'err';

/**
 * The BACKLOG's tabs (Marcus, 2026-09-15). AN OWNERSHIP AXIS, and deliberately
 * not the SHELF / SCORED / WATCHING set the design drew and `18261b5` removed
 * on his instruction — those asked what a game's status on the shelf was, and
 * he wanted them gone. These ask what Steam says about the member: do they own
 * it, and have they played it.
 *
 * PLAYED IS PLAYTIME, NOT A SCORE (his call, asked directly). The app's own
 * copy elsewhere calls a score "what you played", and MY SCORES is the page
 * built on that reading — but this tab filters on `steam_owned_apps`, so a game
 * someone put five hours into and never scored is PLAYED here and absent from
 * MY SCORES. The two surfaces answer different questions on purpose.
 *
 * WISHLISTED SITS BETWEEN OWNED AND UNOWNED (Marcus, 2026-09-15) and the order
 * is the reading: have it · want it · neither. It overlaps UNOWNED rather than
 * partitioning against it — you do not usually wishlist what you own — exactly
 * as PLAYED already sits inside OWNED. These are lenses, not a partition.
 *
 * ITS SOURCE IS A LIVE STEAM READ, NOT A TABLE. Nothing persists the wishlist:
 * `BacklogEntry` is `{id, at}` with no provenance, and no migration has ever
 * made a wishlist table. `ensureWishlist` asks `steam-sync ?op=wishlist` once
 * per session and caches the appids — Marcus's call when asked, over adding a
 * table for a fact Steam serves unkeyed and free.
 */
export type BacklogTabKey = 'all' | 'played' | 'owned' | 'wishlisted' | 'unowned';

export interface MineSlice {
  /** shelves by user key — only the signed-in key is ever filled */
  backlog: Record<string, BacklogEntry[]>;
  /** scores by user key, then by game id (as a string key) */
  scores: Record<string, Record<string, ScoreRec>>;

  /** YOUR SCORE — the game the modal is open on; `0` = closed */
  scId: GameId;
  /** TRACK A GAME */
  trkOpen: boolean;
  trkQ: string;
  /** Steam's answer to `trkQ` — the picker's own, never Discover's `live`. */
  trkRows: Game[];
  trkState: TrackState;
  /** BACKLOG page: the ownership tab, the page's own search, the armed ✕ */
  bkTab: BacklogTabKey;
  bkQ: string;
  bkArm: GameId;
  /** MY SCORES page: its own search (`/` focuses it on desktop) */
  rtQ: string;
  /** `wishImport` is in flight — the P3e-03 busy flag, as `steamFilling`. */
  wishBusy: boolean;
  /**
   * The appids on the member's Steam wishlist — the BACKLOG's WISHLISTED tab.
   *
   * `null` MEANS WE CANNOT CLAIM TO KNOW, and it covers three situations on
   * purpose: nobody has asked yet, the read failed, and Steam answered `open:
   * false`. The tab shows NOTHING in all three rather than reporting an empty
   * wishlist, because an empty answer and an unread one are the same
   * fabrication risk `myLib` is built around — here it is simply whole-list
   * rather than per-appid, since one call answers the entire wishlist.
   *
   * A SET ONLY WHEN STEAM SAID `open: true`. That is the one state in which
   * "this game is not on your wishlist" is a fact rather than a guess.
   */
  wishIds: ReadonlySet<number> | null;
  /**
   * Steam answered, and sent no item list — a PRIVATE wishlist or, very
   * probably, an EMPTY one. **Indistinguishable on the wire** (docs/DISCOVERY.md
   * §7.1a: proto3 omits an empty repeated field), which is why this is its own
   * flag rather than `wishIds: new Set()`: the copy has to own up to both, and
   * an empty set would let the tab claim the wishlist is empty.
   */
  wishClosed: boolean;
  /**
   * `ensureWishlist` has spent its one read this session.
   *
   * NOT DERIVABLE FROM `wishIds`, which is exactly why it exists: that is
   * `null` before the request goes out, while it is in the air, after a failure
   * AND after an `open: false` answer, so it cannot tell a read that happened
   * from one that did not. Without this, a render-path caller re-asks on every
   * paint for any member whose wishlist is private, failed, or slow.
   *
   * STATE, NOT A CLOSURE VARIABLE, unlike `ownByInFlight` next door — a
   * sign-out clears this slice with a `set` and cannot reach a closure, so the
   * next account would inherit "already asked" and never read its own wishlist.
   */
  wishAsked: boolean;

  myBacklog: () => BacklogEntry[];
  soloHas: (id: GameId) => boolean;
  scoreOf: (userKey: string, id: GameId) => number | null;

  soloAdd: (id: GameId) => Promise<void>;
  soloRm: (id: GameId) => Promise<void>;
  /**
   * Pull the linked Steam account's wishlist onto the backlog: mint what the
   * catalog lacks, then one bulk shelve. Games already shelved are skipped.
   * BACKLOG's `⟳ PULL IN YOUR STEAM WISHLIST`, both trees.
   */
  wishImport: () => Promise<void>;
  /**
   * Read the wishlist ONCE PER SESSION and cache its appids for the BACKLOG's
   * WISHLISTED tab. Fire-and-forget; both BACKLOG screens call it on render.
   *
   * THE SAME OP THE IMPORT BUTTON USES, deliberately — Marcus's call over both
   * a table and a new ids-only endpoint. The cost it accepts is that
   * `?op=wishlist` also resolves game NAMES the tab has no use for (the backlog
   * already has them), so a second or so of server work is thrown away. Once
   * per session, and no key, no quota and no throttle are involved
   * (docs/DISCOVERY.md §7.1a), so the waste is bounded and buys a zero-migration
   * tab.
   *
   * SKIPPED WHILE AN IMPORT IS RUNNING: that is the same read, and it publishes
   * its answer here when it lands.
   */
  ensureWishlist: () => void;
  /** `null` clears. Toasts, closes the modal. */
  setScore: (id: GameId, v: number | null) => Promise<void>;

  openScore: (id: GameId) => void;
  closeScore: () => void;
  openTrack: () => void;
  closeTrack: () => void;
  /** Debounced; under the minimum it clears Steam's rows without asking. */
  setTrkQ: (v: string) => void;
  /** The browse itself — what the debounce fires. Exported for tests. */
  trkSearch: () => Promise<void>;
  setBkTab: (t: BacklogTabKey) => void;
  setBkQ: (v: string) => void;
  setRtQ: (v: string) => void;
  /**
   * Arm the ✕ on one backlog tile (`0` disarms). 5 s auto-disarm and a
   * click-away stand-down, the same contract as the round's `rArmSet` — one
   * armed control on screen, ever.
   */
  bkArmSet: (id: GameId) => void;
}

export const createMineSlice: StateCreator<QdStore, [], [], MineSlice> = (set, get) => {
  let armTimer: ReturnType<typeof setTimeout> | null = null;
  let armAway: ((ev: MouseEvent) => void) | null = null;
  const disarmListener = (): void => {
    if (armAway && typeof document !== 'undefined') document.removeEventListener('click', armAway, true);
    armAway = null;
  };
  let trkTimer: ReturnType<typeof setTimeout> | null = null;
  let trkAbort: AbortController | null = null;
  const trkIdle = (): void => {
    if (trkTimer) clearTimeout(trkTimer);
    trkTimer = null;
    if (trkAbort) trkAbort.abort();
    trkAbort = null;
  };

  return {
    backlog: {},
    scores: {},
    scId: 0,
    trkOpen: false,
    trkQ: '',
    trkRows: [],
    trkState: '',
    bkTab: 'all',
    bkQ: '',
    bkArm: 0,
    rtQ: '',
    wishBusy: false,
    wishIds: null,
    wishClosed: false,
    wishAsked: false,

    myBacklog: () => {
      const m = get().me();
      return m ? (get().backlog[m.k] ?? []) : [];
    },
    soloHas: (id) => get().myBacklog().some((e) => e.id === id),
    scoreOf: (userKey, id) => get().scores[userKey]?.[String(id)]?.v ?? null,

    soloAdd: async (id) => {
      const s = get();
      const m = s.me();
      if (!m || s.soloHas(id)) return;
      let g = s.gById(id);
      // A pick from the picker's Steam half names a game the catalog has never
      // carried. Adopt the browse row BEFORE the optimistic write, flagged
      // provisional the way a GOTY steamFill mint is, so `backlogVM` can draw
      // the shelf tile at once instead of dropping an unresolvable entry; the
      // next bootstrap answers with the real row (3061's backlog arm) and
      // replaces it. `browsable()` keeps a provisional row off Discover's
      // local mode, which is right — it is on your shelf, not in the party's
      // catalog.
      if (!g) {
        const row = s.trkRows.find((r) => r.id === id);
        if (row) {
          g = { ...row, prov: true };
          const adopt = g;
          set((cur) => (cur.catalog.some((c) => c.id === id) ? {} : { catalog: [...cur.catalog, adopt] }));
        }
      }
      const entry: BacklogEntry = { id, at: Date.now() };
      await mutate(storeApi(get, set), {
        key: 'backlog:' + id,
        optimistic: (st) => ({ backlog: { ...st.backlog, [m.k]: [...(st.backlog[m.k] ?? []), entry] } }),
        commit: () => s.repo.addBacklog(id),
        toast: 'ON YOUR BACKLOG' + (g ? ' · ' + g.n.toUpperCase() : ''),
      });
    },

    soloRm: async (id) => {
      const s = get();
      const m = s.me();
      if (!m) return;
      // The armed ✕ that commits this stands down with it.
      if (s.bkArm === id) get().bkArmSet(0);
      await mutate(storeApi(get, set), {
        key: 'backlog:' + id,
        optimistic: (st) => ({ backlog: { ...st.backlog, [m.k]: (st.backlog[m.k] ?? []).filter((e) => e.id !== id) } }),
        commit: () => s.repo.removeBacklog(id),
        toast: 'OFF YOUR BACKLOG',
      });
    },

    wishImport: async () => {
      const s = get();
      const m = s.me();
      if (!m || s.wishBusy) return;
      // Linked is the only gate. `profilePublic` is what the last LIBRARY sync
      // saw and may be stale either way, so the server's answer decides — the
      // flag only picks which "closed" copy to show.
      const link = s.steamLink;
      if (!link || !link.linked) {
        s.toast(WISH_COPY.unlinked, true);
        return;
      }
      set({ wishBusy: true });
      // Every `return` below is inside the try, so the flag clears on all of
      // them as well as on a throw.
      try {
        let res: SteamWishlistResult;
        try {
          res = await s.repo.steamWishlist();
        } catch (e) {
          const code = (e as { code?: unknown } | null)?.code;
          s.toast(code === 'NOT_LINKED' ? WISH_COPY.unlinked : WISH_COPY.upstream, true);
          return;
        }
        // The import has just paid for the read the WISHLISTED tab wants, so it
        // publishes it rather than leaving the tab to ask again. Both branches
        // below record an answer: `open: false` is the private-or-empty state,
        // not knowledge of an empty wishlist.
        publishWishlist(set, res);
        if (!res.open) {
          s.toast(link.profilePublic ? WISH_COPY.closed : WISH_COPY.private, true);
          return;
        }
        if (!res.games.length) {
          s.toast(WISH_COPY.none);
          return;
        }

        // Re-read, not `s`: the list may have moved while Steam was answering.
        const have = new Set(get().myBacklog().map((e) => Number(e.id)));
        const fresh = res.games.filter((g) => !have.has(g.appid));
        if (!fresh.length) {
          s.toast(WISH_COPY.nothingNew);
          return;
        }

        // Mint first, exactly as `commitGotyPicks` does and for its reason: the
        // bulk shelve does not mint, and one missing catalog row fails the whole
        // statement. A game that will not mint is left off rather than sinking
        // the other ninety.
        const toMint = fresh.filter((g) => !get().gByAppid(g.appid));
        const minted$ = await settlePooled(
          toMint,
          (g: WishlistGame) =>
            s.repo.ensureGame({
              appid: g.appid,
              name: g.name,
              dev: g.dev,
              releaseYm: '',
              price: null,
              oldPrice: 0,
              upcoming: g.upcoming,
              description: '',
              artUrl: '',
            }),
          MINT_CONCURRENCY,
        );
        const failed = new Set(toMint.filter((_, i) => !minted$[i]).map((g) => g.appid));
        const add = fresh.filter((g) => !failed.has(g.appid));
        if (!add.length) {
          s.toast(WISH_COPY.failed, true);
          return;
        }

        // Before the optimistic write, so `backlogVM` can draw every new tile.
        // Steam's `upcoming` is a fact it just told us, so it rides along on the
        // provisional row; everything `provisionalGame` refuses to invent stays
        // refused.
        const mintedIds = new Set(toMint.map((g) => g.appid));
        const adopt: Game[] = add
          .filter((g) => mintedIds.has(g.appid))
          .map((g) => ({ ...provisionalGame(g), dev: g.dev || '—', up: g.upcoming }));
        if (adopt.length) {
          set((cur) => {
            const known = new Set(cur.catalog.map((c) => c.id));
            const next = adopt.filter((g) => !known.has(g.id));
            return next.length ? { catalog: [...cur.catalog, ...next] } : {};
          });
        }

        const at = Date.now();
        const ids = add.map((g) => g.appid as GameId);
        await mutate(storeApi(get, set), {
          key: 'backlog:wishlist',
          optimistic: (st) => {
            const cur = st.backlog[m.k] ?? [];
            const on = new Set(cur.map((e) => e.id));
            const entries = ids.filter((id) => !on.has(id)).map((id): BacklogEntry => ({ id, at }));
            return { backlog: { ...st.backlog, [m.k]: [...cur, ...entries] } };
          },
          commit: () => s.repo.addBacklogMany(ids),
          toast: WISH_COPY.added(add.length),
        });
        // Cover art for the new tiles; the minted rows have none of their own.
        s.queueAssets(ids.map(Number));
      } finally {
        set({ wishBusy: false });
      }
    },

    setScore: async (id, v) => {
      const s = get();
      const m = s.me();
      if (!m) return;
      const g = s.gById(id);
      // One tap sets and closes (design). Close before the write, so a slow
      // commit never holds a modal open over the tile that already shows it.
      set({ scId: 0 });
      await mutate(storeApi(get, set), {
        key: 'score:' + id,
        optimistic: (st) => {
          const mine = { ...(st.scores[m.k] ?? {}) };
          const was = mine[String(id)];
          if (v === null) delete mine[String(id)];
          // A re-score keeps what it replaced, exactly as the row reads back
          // (3067's trigger sets `prev` from the old score). The same number
          // again is not a re-score: the trigger leaves `prev` alone, so this
          // carries the old one through unchanged.
          else if (was === undefined) mine[String(id)] = { v, at: Date.now() };
          else if (was.v === v) mine[String(id)] = { ...was, at: Date.now() };
          else mine[String(id)] = { v, at: Date.now(), prev: was.v };
          return { scores: { ...st.scores, [m.k]: mine } };
        },
        commit: () => s.repo.setScore(id, v),
        toast: v === null ? 'SCORE CLEARED' : 'SCORED ' + v + (g ? ' · ' + g.n.toUpperCase() : ''),
      });
    },

    openScore: (id) => set({ scId: id }),
    closeScore: () => set({ scId: 0 }),
    openTrack: () => {
      trkIdle();
      set({ trkOpen: true, trkQ: '', trkRows: [], trkState: '' });
    },
    closeTrack: () => {
      trkIdle();
      set({ trkOpen: false });
    },
    setTrkQ: (v) => {
      trkIdle();
      if (v.trim().length < TRACK_MIN_CHARS) {
        set({ trkQ: v, trkRows: [], trkState: '' });
        return;
      }
      // Steam's old rows come down at once — a new question, a fresh answer —
      // while the catalog's matches, which the selector computes, never blink.
      set({ trkQ: v, trkRows: [], trkState: 'busy' });
      trkTimer = setTimeout(() => {
        trkTimer = null;
        void get().trkSearch();
      }, SEARCH_DEBOUNCE_MS);
    },
    trkSearch: async () => {
      const term = get().trkQ.trim();
      if (term.length < TRACK_MIN_CHARS) return;
      if (trkAbort) trkAbort.abort();
      const ctl = new AbortController();
      trkAbort = ctl;
      set({ trkState: 'busy' });
      // Term only. The picker has no tag rail and no filter bar — a shelf is
      // not a board — so nothing narrows what Steam is asked. Page 0 only: the
      // list is a picker, not a browse, and a name that is not in the first
      // page is one more letter away.
      const page = await get()
        .repo.fetchBrowse(
          { term, tags: [], start: 0, sort: null, maxPrice: null, specials: false, comingSoon: false },
          ctl.signal,
        )
        .catch(() => null);
      // Superseded (a later keystroke, or the modal closed): keep quiet.
      if (ctl.signal.aborted || get().trkQ.trim() !== term) return;
      trkAbort = null;
      if (!page) {
        set({ trkRows: [], trkState: 'err' });
        return;
      }
      const dead = !page.fromNetwork && page.items.length === 0;
      // THE ART CAME BACK WITH THE ROWS, and dropping it left every modern
      // appid in the picker as a checkerboard: `artCands` prefers a fetched
      // asset over a guessed legacy URL, and the legacy shapes 404 for anything
      // recent. `slices/browse.ts` merges `page.assets` for exactly this reason;
      // this path is the same `fetchBrowse` response and was simply throwing the
      // map away. Free — no extra round trip.
      set((st) => ({
        trkRows: page.items,
        trkState: dead ? 'err' : 'ok',
        assets: { ...st.assets, ...page.assets },
      }));
    },
    ensureWishlist: () => {
      const s = get();
      // An import is the same read, and it publishes its answer — letting both
      // run would spend two Steam round trips on one question.
      if (s.wishAsked || s.wishBusy) return;
      // Unlinked has nothing to ask about, and asking would answer NOT_LINKED
      // for every member who has not connected Steam. Not marked asked: linking
      // mid-session should make the tab work without a reload.
      const link = s.steamLink;
      if (!link || !link.linked) return;
      set({ wishAsked: true });
      void (async () => {
        try {
          publishWishlist(set, await s.repo.steamWishlist());
        } catch (e) {
          // NOTHING RECORDED, so `wishIds` stays `null` and the tab keeps saying
          // it is still reading rather than reporting an empty wishlist — the
          // same refusal `ensureMyLibrary` makes, and for the same reason: a
          // cached failure here would read as "nothing you shelved is
          // wishlisted", which is a claim we have no basis for.
          //
          // `wishAsked` STAYS TRUE. This is a user-visible second of Steam work
          // on a render path; retrying it every paint after a failure is how a
          // dead endpoint becomes a request storm. A sign-out re-asks, and the
          // import button is the manual retry in the meantime.
          console.error('[qd] wishlist read failed — the WISHLISTED tab stays pending', e);
        }
      })();
    },
    setBkTab: (t) => set({ bkTab: t }),
    setBkQ: (v) => set({ bkQ: v }),
    setRtQ: (v) => set({ rtQ: v }),

    bkArmSet: (id) => {
      if (armTimer) clearTimeout(armTimer);
      armTimer = null;
      disarmListener();
      set({ bkArm: id });
      if (!id) return;
      armTimer = setTimeout(() => {
        armTimer = null;
        if (get().bkArm === id) set({ bkArm: 0 });
      }, R_ARM_MS);
      if (typeof document !== 'undefined') {
        armAway = (ev) => {
          const t = ev.target as Element | null;
          if (t && typeof t.closest === 'function' && t.closest('[data-arm]')) return;
          if (get().bkArm) set({ bkArm: 0 });
          disarmListener();
        };
        // Deferred one tick so the click that armed it is not the click that
        // disarms it.
        setTimeout(() => {
          if (armAway) document.addEventListener('click', armAway, true);
        }, 0);
      }
    },
  };
};
